import React from "react";
import api from "@services/api";
import { useMarketplace } from "@context/MarketplaceContext";
import { loadGoogleMaps } from "@lib/googleMapsLoader";
import "./driver-assign.css";

const todayISO = () => new Date().toISOString().slice(0, 10);
const AMMAN = { lat: 31.9539, lng: 35.9106 };
const PALETTE = ["#2563eb", "#16a34a", "#db2777", "#7c3aed", "#0891b2", "#ca8a04", "#dc2626", "#4d7c0f", "#9333ea", "#0d9488"];
const UNASSIGNED_COLOR = "#9ca3af";

/* ------------------------------------------------------------------ Map */
function AssignMap({ zones, orders, driverColors, selectedDriver, onMarkerClick, showRoutes, height = 460 }) {
  const ref = React.useRef(null);
  const mapRef = React.useRef(null);
  const zonesRef = React.useRef([]);
  const markersRef = React.useRef([]);
  const routesRef = React.useRef([]);
  const infoRef = React.useRef(null);
  const fitRef = React.useRef(false);
  const cbRef = React.useRef(onMarkerClick);
  cbRef.current = onMarkerClick;
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => { let ok = true; loadGoogleMaps().then(() => ok && setReady(true)); return () => { ok = false; }; }, []);

  React.useEffect(() => {
    const g = window.google;
    if (!ready || !g?.maps || !ref.current || mapRef.current) return;
    mapRef.current = new g.maps.Map(ref.current, { center: AMMAN, zoom: 11, mapTypeControl: false, streetViewControl: false });
    infoRef.current = new g.maps.InfoWindow();
  }, [ready]);

  // Zone polygons (light, under markers).
  React.useEffect(() => {
    const g = window.google; const map = mapRef.current;
    if (!ready || !g?.maps || !map) return;
    for (const p of zonesRef.current) p.setMap(null);
    zonesRef.current = Object.entries(zones || {}).map(([, z]) => {
      const coords = (z.coordinates || []).filter((c) => Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng))).map((c) => ({ lat: Number(c.lat), lng: Number(c.lng) }));
      if (coords.length < 3) return null;
      return new g.maps.Polygon({ paths: coords, strokeColor: z.color, strokeOpacity: 0.7, strokeWeight: 1.5, fillColor: z.color, fillOpacity: 0.08, clickable: false, zIndex: 1, map });
    }).filter(Boolean);
  }, [zones, ready]);

  // Order markers (colored by driver) + optional route lines.
  React.useEffect(() => {
    const g = window.google; const map = mapRef.current;
    if (!ready || !g?.maps || !map) return;
    for (const m of markersRef.current) m.setMap(null);
    for (const r of routesRef.current) r.setMap(null);
    markersRef.current = []; routesRef.current = [];

    const bounds = new g.maps.LatLngBounds();
    let any = false;

    for (const o of orders) {
      if (!Number.isFinite(o.lat) || !Number.isFinite(o.lng)) continue;
      any = true;
      bounds.extend({ lat: o.lat, lng: o.lng });
      const color = o.driver_id ? (driverColors[o.driver_id] || UNASSIGNED_COLOR) : UNASSIGNED_COLOR;
      const dim = selectedDriver && o.driver_id !== selectedDriver;
      const marker = new g.maps.Marker({
        position: { lat: o.lat, lng: o.lng }, map, zIndex: dim ? 2 : 4,
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: dim ? 4 : 6, fillColor: color, fillOpacity: dim ? 0.35 : 1, strokeColor: "#fff", strokeWeight: 1 },
      });
      marker.addListener("click", () => {
        infoRef.current.setContent(
          `<div style="font-size:13px"><b>#${o.order_id}</b> ${o.name || ""}<br>${o.area ? o.area.replace(/_/g, " ") : "no area"}${o.slot_label ? " · " + o.slot_label : ""}<br>Driver: <b>${o.driver_name || "—"}</b></div>`
        );
        infoRef.current.open({ map, anchor: marker });
        cbRef.current?.(o.order_id);
      });
      markersRef.current.push(marker);
    }

    if (showRoutes) {
      const byDriver = {};
      for (const o of orders) if (o.driver_id && Number.isFinite(o.lat)) (byDriver[o.driver_id] ||= []).push(o);
      for (const [did, list] of Object.entries(byDriver)) {
        if (selectedDriver && Number(did) !== selectedDriver) continue;
        const path = [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((o) => ({ lat: o.lat, lng: o.lng }));
        if (path.length < 2) continue;
        routesRef.current.push(new g.maps.Polyline({ path, strokeColor: driverColors[did] || "#333", strokeOpacity: 0.85, strokeWeight: 2.5, map, zIndex: 3 }));
      }
    }

    if (any && !fitRef.current) { map.fitBounds(bounds); fitRef.current = true; }
  }, [orders, driverColors, selectedDriver, showRoutes, ready]);

  return (
    <div className="sa-map-wrap">
      {!ready && <div style={{ height, display: "grid", placeItems: "center", background: "#f5f5f5", borderRadius: 8 }}>Loading map…</div>}
      <div ref={ref} style={{ width: "100%", height, borderRadius: 8, display: ready ? "block" : "none" }} />
    </div>
  );
}

/* --------------------------------------------------- Driver coverage editor */
function CoveragePanel({ zones, drivers, onChanged }) {
  const [rows, setRows] = React.useState([]);
  const [savingId, setSavingId] = React.useState(null);
  React.useEffect(() => {
    setRows((drivers || []).map((d) => ({ ...d, zoneSet: new Set((d.areas || []).map((a) => a.zone_key)) })));
  }, [drivers]);

  const toggle = (i, zk) => setRows((rs) => rs.map((d, idx) => {
    if (idx !== i) return d;
    const s = new Set(d.zoneSet); s.has(zk) ? s.delete(zk) : s.add(zk); return { ...d, zoneSet: s };
  }));
  const save = async (i) => {
    const d = rows[i]; setSavingId(d.driver_id);
    try {
      await api.put(`/assignments/drivers/${d.driver_id}/areas`, { areas: [...d.zoneSet].map((zk) => ({ zone_key: zk })) });
      onChanged?.();
    } finally { setSavingId(null); }
  };

  return (
    <div className="sa-coverage">
      {rows.map((d, i) => (
        <div className="sa-cov-row" key={d.driver_id}>
          <strong>{d.name}</strong>
          <div className="sa-cov-zones">
            {Object.entries(zones).map(([name, z]) => (
              <label key={name} className={"sa-cov-zone" + (d.zoneSet.has(name) ? " on" : "")}>
                <input type="checkbox" checked={d.zoneSet.has(name)} onChange={() => toggle(i, name)} />
                <span className="da-dot" style={{ background: z.color }} />
                {z.name_ar || name.replace(/_/g, " ")}
              </label>
            ))}
          </div>
          <button className="oc-btn" onClick={() => save(i)} disabled={savingId === d.driver_id}>{savingId === d.driver_id ? "…" : "Save"}</button>
        </div>
      ))}
      {!rows.length && <div className="da-muted">No active drivers.</div>}
    </div>
  );
}

/* --------------------------------------------------------------- Main page */
export default function SmartAssignPage() {
  const { marketplaces } = useMarketplace();
  const franchiseOptions = (marketplaces && marketplaces.length)
    ? marketplaces.map((m) => ({ id: Number(m.franchise_id), name: m.name }))
    : [{ id: 0, name: "WashyWash" }, { id: 4, name: "FlashWash" }];

  const [date, setDate] = React.useState(todayISO());
  const [franchiseId, setFranchiseId] = React.useState(franchiseOptions[0].id);
  const [type, setType] = React.useState("dropoff");
  const [preferSameArea, setPreferSameArea] = React.useState(true);
  const [maxPerSlot, setMaxPerSlot] = React.useState("");
  const [showRoutes, setShowRoutes] = React.useState(true);
  const [showCoverage, setShowCoverage] = React.useState(false);

  const [zones, setZones] = React.useState({});
  const [driversRaw, setDriversRaw] = React.useState([]); // with areas (for coverage)
  const [proposal, setProposal] = React.useState(null);
  const [selectedDriver, setSelectedDriver] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [committing, setCommitting] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const loadMeta = React.useCallback(() => {
    api.get("/map-zones").then((r) => setZones(r.data?.data || {})).catch(() => {});
    api.get("/assignments/drivers").then((r) => setDriversRaw(r.data?.data || [])).catch(() => {});
  }, []);
  React.useEffect(() => { loadMeta(); }, [loadMeta]);

  const run = async () => {
    setLoading(true); setMsg(null); setProposal(null); setSelectedDriver(null);
    try {
      const { data } = await api.post("/assignments/preview", {
        date, franchise_id: franchiseId, type, prefer_same_area: preferSameArea,
        max_per_slot: maxPerSlot === "" ? undefined : Number(maxPerSlot),
      });
      setProposal(data);
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.message || "Failed to build the proposal." });
    } finally { setLoading(false); }
  };

  const loadCurrent = async () => {
    setLoading(true); setMsg(null); setSelectedDriver(null);
    try {
      const { data } = await api.get("/assignments/current", { params: { date, franchise_id: franchiseId, type } });
      setProposal(data);
      setMsg({ type: "ok", text: `Loaded last saved result — ${data.committed_count || 0} order(s) already assigned. Adjust and re-confirm to update.` });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.message || "Failed to load the saved result." });
    } finally { setLoading(false); }
  };

  const reassign = (orderId, driverId) => setProposal((p) => ({
    ...p,
    orders: p.orders.map((o) => o.order_id !== orderId ? o
      : { ...o, driver_id: driverId, driver_name: (p.drivers.find((d) => d.driver_id === driverId)?.name) ?? null, reason: driverId ? null : "manually unassigned" }),
  }));
  const onDrop = (e, driverId) => { e.preventDefault(); const id = Number(e.dataTransfer.getData("text/plain")); if (id) reassign(id, driverId); };

  const commit = async () => {
    const assignments = (proposal?.orders || []).filter((o) => o.driver_id)
      .map((o) => ({ order_id: o.order_id, driver_id: o.driver_id, slot_id: o.slot_id, sort_order: o.sort_order ?? 0 }));
    if (!assignments.length) { setMsg({ type: "err", text: "Nothing to confirm." }); return; }
    setCommitting(true); setMsg(null);
    try {
      const { data } = await api.post("/assignments/commit", { type, assignments });
      setMsg({ type: "ok", text: `Confirmed — ${data.committed} orders assigned to drivers.` });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.message || "Failed to confirm." });
    } finally { setCommitting(false); }
  };

  // Stable driver color per driver_id.
  const driverColors = React.useMemo(() => {
    const map = {};
    (proposal?.drivers || []).forEach((d, i) => { map[d.driver_id] = PALETTE[i % PALETTE.length]; });
    return map;
  }, [proposal]);

  const zoneColor = (area) => zones[area]?.color || UNASSIGNED_COLOR;
  const byDriver = {}; const unassigned = [];
  for (const o of proposal?.orders || []) { if (o.driver_id) (byDriver[o.driver_id] ||= []).push(o); else unassigned.push(o); }
  const sortRoute = (arr) => [...arr].sort((a, b) => (a.slot_label || "").localeCompare(b.slot_label || "") || (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const Card = ({ o }) => (
    <div className="sa-card" draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", String(o.order_id))} title={o.reason || ""}>
      <span className="sa-dot" style={{ background: zoneColor(o.area) }} />
      <div className="sa-card-main">
        <div className="sa-card-top">#{o.order_id} <span className="da-muted">{o.name || ""}</span></div>
        <div className="sa-card-sub">{o.area ? (zones[o.area]?.name_ar || o.area.replace(/_/g, " ")) : "no area"}{o.slot_label ? ` · ${o.slot_label}` : ""}</div>
      </div>
      <select className="sa-move" value={o.driver_id || ""} onChange={(e) => reassign(o.order_id, e.target.value ? Number(e.target.value) : null)}>
        <option value="">— unassigned —</option>
        {(proposal?.drivers || []).map((d) => <option key={d.driver_id} value={d.driver_id}>{d.name}</option>)}
      </select>
    </div>
  );

  return (
    <div className="oc-content sa-page">
      <div className="da-head">
        <div>
          <h2>Assign Orders to Agents</h2>
          <p className="da-muted">Agents are the Laundry Agent users who deliver. Everything in one place: set agent areas, auto-assign the day's orders by area + best timeslot, review on the map and the board, then confirm. Use “Load last result” to see and edit the last saved assignment.</p>
        </div>
      </div>

      <div className="oc-card sa-controls">
        <label>Date <input className="oc-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label>Marketplace
          <select className="oc-input" value={franchiseId} onChange={(e) => setFranchiseId(Number(e.target.value))}>
            {franchiseOptions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </label>
        <label>Type
          <select className="oc-input" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="dropoff">Delivery (drop-off)</option>
            <option value="pickup">Pickup</option>
          </select>
        </label>
        <label>Max/slot <input className="oc-input" type="number" min="1" style={{ width: 80 }} value={maxPerSlot} onChange={(e) => setMaxPerSlot(e.target.value)} placeholder="none" /></label>
        <label className="sa-check"><input type="checkbox" checked={preferSameArea} onChange={(e) => setPreferSameArea(e.target.checked)} /> Cluster by area</label>
        <label className="sa-check"><input type="checkbox" checked={showRoutes} onChange={(e) => setShowRoutes(e.target.checked)} /> Route lines</label>
        <button className="oc-btn primary" onClick={run} disabled={loading}>{loading ? "Assigning…" : "⚡ Auto-assign"}</button>
        <button className="oc-btn" onClick={loadCurrent} disabled={loading} title="Load the last saved assignment for this date/type">↩ Load last result</button>
        <button className="oc-btn" onClick={() => setShowCoverage((s) => !s)}>{showCoverage ? "Hide" : "Agent areas"}</button>
      </div>

      {showCoverage && (
        <div className="oc-card" style={{ marginBottom: 12 }}>
          <CoveragePanel zones={zones} drivers={driversRaw} onChanged={loadMeta} />
        </div>
      )}

      {msg && <div className={`da-msg ${msg.type}`}>{msg.text}</div>}

      {/* Map — always visible; shows zones + (once assigned) colored order markers + routes */}
      <div className="oc-card" style={{ padding: 8, marginBottom: 12 }}>
        <AssignMap
          zones={zones}
          orders={proposal?.orders || []}
          driverColors={driverColors}
          selectedDriver={selectedDriver}
          showRoutes={showRoutes}
          onMarkerClick={() => {}}
          height={440}
        />
        {proposal && (
          <div className="sa-legend">
            {(proposal.drivers || []).map((d) => (
              <span key={d.driver_id} className={"sa-leg" + (selectedDriver === d.driver_id ? " on" : "")} onClick={() => setSelectedDriver(selectedDriver === d.driver_id ? null : d.driver_id)}>
                <span className="sa-dot" style={{ background: driverColors[d.driver_id] }} /> {d.name} ({(byDriver[d.driver_id] || []).length})
              </span>
            ))}
            <span className="sa-leg"><span className="sa-dot" style={{ background: UNASSIGNED_COLOR }} /> Unassigned ({unassigned.length})</span>
          </div>
        )}
      </div>

      {proposal && (
        <>
          <div className="sa-summary">
            <b>{proposal.summary.total}</b> orders · <b style={{ color: "#16a34a" }}>{proposal.summary.assigned}</b> assigned · <b style={{ color: "#dc2626" }}>{proposal.summary.unassigned}</b> unassigned
            <div className="da-spacer" />
            <button className="oc-btn primary" onClick={commit} disabled={committing}>{committing ? "Confirming…" : "✓ Confirm assignments"}</button>
          </div>

          <div className="sa-board">
            {(proposal.drivers || []).map((d) => (
              <div className={"sa-col" + (selectedDriver === d.driver_id ? " sel" : "")} key={d.driver_id}
                   onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, d.driver_id)}
                   style={{ borderTop: `3px solid ${driverColors[d.driver_id]}` }}>
                <div className="sa-col-head" onClick={() => setSelectedDriver(selectedDriver === d.driver_id ? null : d.driver_id)}>
                  {d.name} <span className="sa-count">{(byDriver[d.driver_id] || []).length}</span>
                  <div className="sa-col-zones">{(d.zones || []).map((z) => <span key={z} className="sa-dot" style={{ background: zoneColor(z) }} title={z} />)}</div>
                </div>
                <div className="sa-col-body">
                  {sortRoute(byDriver[d.driver_id] || []).map((o) => <Card key={o.order_id} o={o} />)}
                  {!(byDriver[d.driver_id] || []).length && <div className="da-muted sa-empty">Drop orders here</div>}
                </div>
              </div>
            ))}
            <div className="sa-col unassigned" onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, null)}>
              <div className="sa-col-head">Unassigned <span className="sa-count">{unassigned.length}</span></div>
              <div className="sa-col-body">
                {unassigned.map((o) => <Card key={o.order_id} o={o} />)}
                {!unassigned.length && <div className="da-muted sa-empty">— none —</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
